import React, { useState } from 'react';
import { toast } from 'sonner';
import { Layers, ShieldCheck, CalendarRange, MessageCircleQuestion, Save, Loader2, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { updateAssessmentConfig, apiError } from '../../services/assessmentApi';

const TOGGLES = [
  { key: 'show_integrity', icon: ShieldCheck, title: 'Integrity monitoring',
    text: 'Tab switches, blocked pastes, time away and integrity risk. When off, the recommendation is based on the score only.' },
  { key: 'include_learning_plan', icon: CalendarRange, title: 'Personalised learning plan',
    text: 'When off, the AI skips writing the plan to save AI usage. Turn it back on and older reports get a plan the next time they are opened.' },
  { key: 'include_interview_probes', icon: MessageCircleQuestion, title: 'Suggested interview probes',
    text: 'Follow-up questions for the hiring manager to ask in the interview.' },
];

const ToggleRow = ({ icon: Icon, title, text, checked, onChange, testid }) => (
  <div className="flex items-start gap-4 py-4 border-b last:border-0">
    <span className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></span>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold">{title}</p>
      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{text}</p>
    </div>
    <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground shrink-0 pt-1">
      {checked ? 'Included' : 'Hidden'}
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </label>
  </div>
);

export const ConfigPanel = ({ test, onSaved }) => {
  const initial = { min_skills: test.min_skills, ...test.report_config };
  const [base, setBase] = useState(initial);
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const dirty = JSON.stringify(base) !== JSON.stringify(form);
  const options = Array.from({ length: test.max_skills - (test.base_min_skills || 4) + 1 }, (_, i) => (test.base_min_skills || 4) + i);

  const save = async () => {
    setBusy(true);
    try {
      const r = await updateAssessmentConfig(test.id, form);
      const next = { min_skills: r.min_skills, ...r.report_config };
      setBase(next); setForm(next);
      toast.success('Configuration saved');
      onSaved?.();
    } catch (e) { toast.error(apiError(e, 'Could not save configuration')); } finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_1.4fr] gap-6 items-start" data-testid="assessment-config-panel">
      <section className="rounded-2xl border bg-card p-6 space-y-4">
        <div className="flex items-center gap-2"><Layers className="h-4 w-4 text-primary" /><h3 className="text-sm font-bold">Candidate skill selection</h3></div>
        <div className="space-y-1.5">
          <p className="text-xs font-semibold text-muted-foreground">Minimum skills a candidate must pick</p>
          <Select value={String(form.min_skills)} onValueChange={(v) => setForm({ ...form, min_skills: Number(v) })}>
            <SelectTrigger className="w-40" data-testid="config-min-skills-select"><SelectValue /></SelectTrigger>
            <SelectContent>{options.map((n) => <SelectItem key={n} value={String(n)} data-testid={`config-min-skills-option-${n}`}>{n} skills</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <p className="text-sm" data-testid="config-min-skills-summary">
          Candidates pick <b>{form.min_skills === test.max_skills ? `exactly ${test.max_skills}` : `${form.min_skills}–${test.max_skills}`}</b> of {test.skills.length} skills.
        </p>
        <p className="text-xs text-muted-foreground leading-relaxed">Applies to the public link and to personal links without their own minimum. You can set a different minimum when creating a personal link.</p>
      </section>
      <section className="rounded-2xl border bg-card p-6">
        <h3 className="text-sm font-bold">Shared report &amp; PDF content</h3>
        <p className="text-xs text-muted-foreground mt-1 flex items-start gap-1.5"><Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          Applies instantly to every shared link and PDF, old and new. Inside HireFast you always see everything, marked “Hidden from shared report”.</p>
        <div className="mt-2">
          {TOGGLES.map((t) => <ToggleRow key={t.key} {...t} checked={!!form[t.key]} onChange={(v) => setForm({ ...form, [t.key]: v })} testid={`config-toggle-${t.key}`} />)}
        </div>
      </section>
      <div className="lg:col-span-2 flex flex-wrap items-center justify-end gap-3">
        {test.config_updated_by && <span className="text-xs text-muted-foreground mr-auto" data-testid="config-last-updated">Last changed by {test.config_updated_by} · {new Date(test.config_updated_at).toLocaleString()}</span>}
        <Button variant="ghost" disabled={!dirty || busy} onClick={() => setForm(base)} data-testid="config-reset-btn">Discard</Button>
        <Button className="gap-2" disabled={!dirty || busy} onClick={save} data-testid="config-save-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save configuration
        </Button>
      </div>
    </div>
  );
};

export default ConfigPanel;
